import { SagaIterator } from '@redux-saga/types';
import { put, select, delay, cancel, call, retry } from 'redux-saga/effects';
import { getSubjectAreaPage } from './sagaUtils';
import {
	fetchSubjectAreaSubjectId,
	fetchSubjectAreaActiveExamLevel,
	getSelectedSubjectAreaComparisons,
} from 'features/subjectArea/redux/selectors';
import { SubjectAreaPages } from 'features/subjectArea/enums';
import {
	getAppliedGradepoints,
	getBenchmarkSetName,
	getRawAvailableComparisonsByAppliedKeys,
} from 'features/app/redux/context';
import {
	fetchAttainmentProfileData,
	SubjectAreaAttainmentProfileDataResponse,
} from '../../../../api/subjectAreaAPI';
import { Context } from 'features/app/types/index';
import { flattenResponse } from '../../../../utils/agGridUtils/agGridComparisonsHelpers';
import { setsubjectAreaLoading } from '../slice';
import { getUdfTypeAndValue } from 'features/subjectPage/redux';

//TODO: revisit this if revisited to make area dynamic
/**
 * Saga workflow to handle the changing of the comparisons within
 */
export function* changeComprisonsFlow(): SagaIterator<void> {
	//Fetch the comparisons applied to the subject area - this will match the Subject overview and Subject page (depricated)
	const appliedComparisons = yield select(getSelectedSubjectAreaComparisons);

	// Removing all comparisons should filter the already-loaded base data in-memory,
	// not refetch. Bail before touching the loading flag so removal is instant and
	// doesn't flash the grid out (the page gates on subjectArea.loading). #51930
	if (!appliedComparisons || appliedComparisons.length === 0) {
		return;
	}

	//Set Subject Area Loading
	yield put(setsubjectAreaLoading(true));

	//Figure out what page we are on
	const page = yield call(getSubjectAreaPage);

	//Find out what the current subject area Id
	const subjectAreaIdSet = yield select(fetchSubjectAreaSubjectId);

	//If the subject Id is undefined or we arent on the subject area pages cancel
	if (subjectAreaIdSet === undefined && page !== SubjectAreaPages) {
		yield put(setsubjectAreaLoading(false));
		yield cancel();
	}

	//Find out what examlevel we are on - this will be 0 but will allow future reusuablity with the selectors
	const currentExamLevel = yield select(fetchSubjectAreaActiveExamLevel);

	//Format the applied the comparisons to have comparison ids
	const availableComparisons = yield select(
		getRawAvailableComparisonsByAppliedKeys(appliedComparisons)
	);

	//check the current udf values
	const udfTypeAndValue: { type: string; value: string } = yield select(getUdfTypeAndValue);

	//Benchmark set
	const benchmarkSet: string = yield select(getBenchmarkSetName);

	//Applied Gradepoints
	const gradepoints: Context.Gradepoint[] = yield select(getAppliedGradepoints);

	const SECOND = 1000;
	try {
		//Fetch the grid and graph comparison data
		const response: ResponseBuilder<SubjectAreaAttainmentProfileDataResponse> = yield retry(
			3, // Try calling 3 times
			10 * SECOND, // with a delay of 10 seconds
			fetchAttainmentProfileData, // API to call
			page, // With these args
			currentExamLevel,
			benchmarkSet,
			gradepoints,
			[subjectAreaIdSet], // fetchAttainmentProfileData expects subjectId as a number[]
			udfTypeAndValue,
			availableComparisons
		);

		//Check the response builder to see if any errors occurred or response is empty
		if (!response || response.hasError) {
			const errorMessage = !response
				? 'Comparison data is empty'
				: (response.errors[0] as any).message || response.errors[0].Message;

			//If theres a problem deal with it
			yield put({
				type: `subjectAreaAttainmentProfile/setComparisonDataFail`,
				payload: errorMessage,
			});
			yield cancel();
		}

		//Add data if successful
		yield put({
			type: `subjectAreaAttainmentProfile/setAllGraphComparisonDataSuccess`,
			payload: response.responseObject.graph.comparisons,
		});

		// Flatten the gridValues object in the response
		const flatten = flattenResponse(response.responseObject.grid.comparisons);

		// put the data in redux
		yield put({
			type: `subjectAreaAttainmentProfile/setAllComparisonDataSuccess`,
			payload: flatten,
		});
		//  set the expanded rows
		yield put({
			type: `subjectAreaAttainmentProfile/setAllExpandedComparisonRows`,
			payload: flatten.allIds,
		});

		//Allow state to update
		yield delay(1000);
	} catch (e) {
		//Error handling
		yield put({
			type: `subjectAreaAttainmentProfile/setComparisonDataFail`,
			payload: e,
		});
	} finally {
		// Guaranteed to run on success, error AND saga cancellation (takeLatest).
		// The grid now gates on subjectArea.loading, so this must always clear or the
		// spinner would be left stuck if a rapid re-trigger cancels this run. #51930
		yield put(setsubjectAreaLoading(false));
	}
}
